import { useCustomerDataQualityGate } from '@components/UserGroupDataQuality/useCustomerDataQualityCheckout';
import { Trans } from '@lingui/react/macro';
import { BookingErrorCode, type BookingRecordDetailsCustomer } from '@navio/nsdk';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiButton, NoddiButtonProps } from 'noddi-ui-web';
import { customerRoutes, getFirstErrorCode } from 'noddi-util';
import { useAuthContext } from 'noddi-web';
import { forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { routePaymentSession } from '../../utils/paymentSession';
import { ApiErrorMessageWithTrans } from '../Error/ApiErrorMessageWithTrans';

type InitiatePaymentSessionButtonProps = (
  | {
      booking: BookingRecordDetailsCustomer;
      orderId?: never;
    }
  | {
      booking?: never;
      orderId: number;
    }
) & {
  className?: string;
  label?: React.ReactNode;
  variant?: NoddiButtonProps['variant'];
};

export const InitiatePaymentSessionButton = forwardRef<HTMLDivElement, InitiatePaymentSessionButtonProps>(
  ({ orderId, booking, className, label, variant = 'success' }, ref) => {
    const navigate = useNavigate();
    const { userData } = useAuthContext();

    // The session is attributed to the booking's own customer, so another
    // signed-in user cannot save that profile. Let the attempt through.
    const isOwnBooking = booking != null && userData?.user.id === booking.user.id;

    const {
      mutateAsync: createPaymentSession,
      isPending: isPaymentSessionPending,
      error: createPaymentSessionError
    } = noddiAsync.usePost({
      type: URLKeys.postOrderPaymentSession,
      queryConfig: {
        onSuccess: ({ data }) => {
          routePaymentSession({
            session: data,
            // oxlint-disable-next-line typescript/no-misused-promises -- routePaymentSession takes navigate typed () => void; react-router's navigate returns a promise that only tracks lazy-route loading
            navigate,
            getEmbeddedPath: (session) => customerRoutes.payForBooking.getPath({ sessionId: session.sessionId })
          });
        }
      }
    });
    const {
      mutateAsync: createBookingPaymentSession,
      isPending: isBookingPaymentSessionPending,
      error: createBookingPaymentSessionError
    } = noddiAsync.usePost({
      type: URLKeys.postBookingPaymentSessionFromSlug,
      queryConfig: {
        onSuccess: ({ data }) => {
          navigate(customerRoutes.payForBooking.getPath({ sessionId: data.sessionId, countryCode: data.countryCode }));
        },
        onError: async (error, { slug }) => {
          // The payment webhook can confirm the booking after this page loaded it, so
          // the button was rendered from a stale status. Re-read the booking so the
          // page shows its real state instead of a pay button the backend refuses.
          if (getFirstErrorCode(error) === BookingErrorCode.BOOKING_NOT_PAYABLE_FOR_PAYMENT_SESSION) {
            await invalidateQueryExactMatch({ urlKey: URLKeys.getBookingInfo, input: { slug } });
          }
        }
      }
    });

    const isPending = isPaymentSessionPending || isBookingPaymentSessionPending;
    const error = createBookingPaymentSessionError || createPaymentSessionError;

    const startPaymentSession = async () => {
      if (booking) {
        await createBookingPaymentSession({ slug: booking.slug });
        return;
      }

      await createPaymentSession({ orderId });
    };

    // The page's sheet belongs to the signed-in customer. Someone else's booking pays without it.
    const dataQuality = useCustomerDataQualityGate();

    return (
      <div ref={ref}>
        <NoddiButton
          className={className}
          variant={variant}
          // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs createBookingPaymentSession(); React discards the returned promise, so a rejection is unhandled unless the body catches
          onClick={async () => {
            if (
              isOwnBooking &&
              dataQuality.holdForDataQuality({
                willPay: true,
                onSaved: () => {
                  void startPaymentSession();
                }
              }) !== 'clear'
            ) {
              return;
            }

            await startPaymentSession();
          }}
          loading={isPending}
          disabled={isPending || (isOwnBooking && dataQuality.isChecking)}
        >
          {label ?? <Trans>Pay</Trans>}
        </NoddiButton>
        {error && <ApiErrorMessageWithTrans error={error} />}
      </div>
    );
  }
);

InitiatePaymentSessionButton.displayName = 'InitiatePaymentSessionButton';
